import type {ComponentStageData, ComponentStageHeuristicDefinition, PluginContext} from '../../types'
import {
    BASE_HORIZONTAL_PADDING,
    CENTER_COLUMN_PINNED_RANGES,
    CENTER_ROW_PINNED_RANGES,
    CORNER_PINNED_RANGES,
    CTX_ABSOLUTE_AVAILABLE_WIDTH
} from './constants'
import {SemanticTypes} from '../../preprocess/conversionDataBuilders'
import {getMeasurementsPointer, getSourceLayoutPointer, getTargetLayoutPointer} from '../../conversionDal/utils/pointer'
import {getParent, updateTargetLayout, predicatePinnedHeuristic, isPinnedToContainer} from '../common'
import type {Pointer} from '@wix/document-services-types'
import {deepClone} from '@wix/wix-immutable-proxy'
import _ from 'lodash'
import {Orientation} from '../../enums'

export const getAvailableWidthKey = (componentPointer: Pointer) =>
    `${CTX_ABSOLUTE_AVAILABLE_WIDTH}-${componentPointer.id}`

const getAvailableWidth = (contextHelper: PluginContext, componentPointer: Pointer) =>
    contextHelper.get(getAvailableWidthKey(componentPointer))

const getAvailableWidthForParent = (componentStageData: ComponentStageData, contextHelper: PluginContext): number => {
    const {componentPointer, conversionDal, config} = componentStageData
    const parentPointer = getParent(conversionDal, componentPointer)
    return parentPointer ? getAvailableWidth(contextHelper, parentPointer) : config.targetCanvasSize
}

const updateComponentWidth = (componentStageData: ComponentStageData, contextHelper: PluginContext, width: number) => {
    const {conversionDal, componentPointer} = componentStageData
    const {componentLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {}
    const availableWidth = getAvailableWidthForParent(componentStageData, contextHelper)
    contextHelper.set(getAvailableWidthKey(componentPointer), width)

    updateTargetLayout(componentStageData, {
        componentLayout: {
            ...componentLayout,
            width: {
                value: (width / availableWidth) * 100,
                type: 'percentage'
            },
            type: 'ComponentLayout'
        }
    })
}

const updatePinnedHorizontalMargins = (
    componentStageData: ComponentStageData,
    target: number | undefined,
    contextHelper: PluginContext
) => {
    const {conversionDal, componentPointer} = componentStageData
    const {itemLayout} = deepClone(conversionDal.get(getSourceLayoutPointer(componentPointer)))
    const {itemLayout: targetItemLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer))
    const {margins, alignSelf, justifySelf} = itemLayout
    const {left, right} = margins

    const marginToUpdate = left?.value > 0 ? left : right
    if (target) marginToUpdate.value = target

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, targetItemLayout, {
            alignSelf,
            justifySelf,
            margins: {
                left,
                right
            },
            type: 'FixedItemLayout'
        })
    })

    const availableWidth = getAvailableWidthForParent(componentStageData, contextHelper)
    contextHelper.set(
        getAvailableWidthKey(componentPointer),
        availableWidth - marginToUpdate.value - BASE_HORIZONTAL_PADDING
    )
}

//TODO: change this once we have a way heuristic to pinned containers
const updatePinnedToContainerHorizontalMargins = (
    componentStageData: ComponentStageData,
    target: number | undefined,
    contextHelper: PluginContext
) => {
    const {conversionDal, componentPointer} = componentStageData
    const {itemLayout} = deepClone(conversionDal.get(getSourceLayoutPointer(componentPointer)))
    const {itemLayout: targetItemLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer))
    const {margins, alignSelf, justifySelf} = itemLayout
    const {left, right} = margins

    const marginToUpdate = left?.value > 0 ? left : right
    if (target) marginToUpdate.value = target

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, targetItemLayout, {
            alignSelf,
            justifySelf,
            margins: {
                left,
                right
            },
            type: 'GridItemLayout'
        })
    })

    const availableWidth = getAvailableWidthForParent(componentStageData, contextHelper)
    contextHelper.set(
        getAvailableWidthKey(componentPointer),
        availableWidth - marginToUpdate.value - BASE_HORIZONTAL_PADDING
    )
}

const updateHorizontalMargins = (
    componentStageData: ComponentStageData,
    contextHelper: PluginContext,
    left: number
) => {
    const {conversionDal, componentPointer} = componentStageData
    const {itemLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer)))
    const availableWidth = getAvailableWidthForParent(componentStageData, contextHelper)
    contextHelper.set(getAvailableWidthKey(componentPointer), availableWidth - 2 * left)

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, itemLayout, {
            margins: {
                left: {
                    value: (left / availableWidth) * 100,
                    type: 'percentage'
                },
                right: {
                    value: (left / availableWidth) * 100,
                    type: 'percentage'
                }
            }
        })
    })
}

export const horizontalMarginsHeuristics: ComponentStageHeuristicDefinition[] = [
    ...CORNER_PINNED_RANGES.map(({min, max, target}) => ({
        predicate: (componentStageData: ComponentStageData) =>
            predicatePinnedHeuristic(
                componentStageData,
                Orientation.Horizontal,
                min,
                max,
                (justifySelf, alignSelf) => alignSelf !== 'center' && justifySelf !== 'center'
            ),
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            //TODO: change this once we have a way heuristic to pinned containers
            if (isPinnedToContainer(componentStageData)) {
                updatePinnedToContainerHorizontalMargins(componentStageData, target, contextHelper)
                return
            }
            updatePinnedHorizontalMargins(componentStageData, target, contextHelper)
        },
        heuristicCode: `CORNER PINNED ELEMENT ${min}-${max} -> ${target}`
    })),
    ...CENTER_COLUMN_PINNED_RANGES.map(({min, max, target}) => ({
        predicate: (componentStageData: ComponentStageData) =>
            predicatePinnedHeuristic(
                componentStageData,
                Orientation.Horizontal,
                min,
                max,
                justifySelf => justifySelf === 'center'
            ),
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            //TODO: change this once we have a way heuristic to pinned containers
            if (isPinnedToContainer(componentStageData)) {
                updatePinnedToContainerHorizontalMargins(componentStageData, target, contextHelper)
                return
            }
            updatePinnedHorizontalMargins(componentStageData, target, contextHelper)
        },
        heuristicCode: `CENTER COLUMNED PINNED ELEMENT ${min}-${max} -> ${target}`
    })),
    ...CENTER_ROW_PINNED_RANGES.map(({min, max, target}) => ({
        predicate: (componentStageData: ComponentStageData) =>
            predicatePinnedHeuristic(
                componentStageData,
                Orientation.Horizontal,
                min,
                max,
                (justifySelf, alignSelf) => alignSelf === 'center'
            ),
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            //TODO: change this once we have a way heuristic to pinned containers
            if (isPinnedToContainer(componentStageData)) {
                updatePinnedToContainerHorizontalMargins(componentStageData, target, contextHelper)
                return
            }
            updatePinnedHorizontalMargins(componentStageData, target, contextHelper)
        },
        heuristicCode: `CENTER ROWED PINNED ELEMENT ${min}-${max} -> ${target}`
    })),
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.SECTION,
        apply: ({componentPointer, config}: ComponentStageData, contextHelper: PluginContext) =>
            contextHelper.set(getAvailableWidthKey(componentPointer), config.targetCanvasSize),
        heuristicCode: 'SECTION X'
    },
    {
        predicate: () => true,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateHorizontalMargins(componentStageData, contextHelper, BASE_HORIZONTAL_PADDING),
        heuristicCode: 'FALLBACK X'
    }
]

export const widthHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.SECTION,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateComponentWidth(componentStageData, contextHelper, componentStageData.config.targetCanvasSize),
        heuristicCode: 'SECTION WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.BOX,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateComponentWidth(
                componentStageData,
                contextHelper,
                getAvailableWidth(contextHelper, componentStageData.componentPointer)
            ),
        heuristicCode: 'BOX WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer, initialSnapshot}: ComponentStageData) => {
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            return (
                conversionDal.get(componentPointer).semanticType === SemanticTypes.IMAGE &&
                originalMeasurements.width <= 200
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer, initialSnapshot} = componentStageData
            const availableWidth = getAvailableWidth(contextHelper, componentPointer)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            updateComponentWidth(
                componentStageData,
                contextHelper,
                Math.min(originalMeasurements.width, availableWidth)
            )
        },
        heuristicCode: 'NARROW IMAGE WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.IMAGE,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateComponentWidth(
                componentStageData,
                contextHelper,
                getAvailableWidth(contextHelper, componentStageData.componentPointer)
            ),
        heuristicCode: 'IMAGE WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer, initialSnapshot}: ComponentStageData) => {
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            return (
                conversionDal.get(componentPointer).semanticType === SemanticTypes.SVG &&
                originalMeasurements.width <= 100
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer, initialSnapshot} = componentStageData
            const availableWidth = getAvailableWidth(contextHelper, componentPointer)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            updateComponentWidth(
                componentStageData,
                contextHelper,
                Math.min(originalMeasurements.width, availableWidth)
            )
        },
        heuristicCode: 'NARROW SVG WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.SVG,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer, initialSnapshot} = componentStageData
            const availableWidth = getAvailableWidth(contextHelper, componentPointer)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            updateComponentWidth(
                componentStageData,
                contextHelper,
                Math.min(originalMeasurements.width * 0.7, availableWidth)
            )
        },
        heuristicCode: 'SVG WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.TEXT,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateComponentWidth(
                componentStageData,
                contextHelper,
                getAvailableWidth(contextHelper, componentStageData.componentPointer)
            ),
        heuristicCode: 'TEXT WIDTH'
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            const {initialSnapshot, conversionDal, componentPointer} = componentStageData
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            return (
                conversionDal.get(componentPointer).semanticType === SemanticTypes.HORIZONTAL_LINE &&
                originalMeasurements.width <= 200
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {initialSnapshot, componentPointer} = componentStageData
            const availableWidth = getAvailableWidth(contextHelper, componentPointer)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            updateComponentWidth(
                componentStageData,
                contextHelper,
                Math.min(originalMeasurements.width, availableWidth)
            )
        },
        heuristicCode: 'NARROW HORIZONTAL LINE WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.HORIZONTAL_LINE,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateComponentWidth(
                componentStageData,
                contextHelper,
                getAvailableWidth(contextHelper, componentStageData.componentPointer)
            ),
        heuristicCode: 'HORIZONTAL LINE WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.BUTTON,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {conversionDal, componentPointer} = componentStageData
            const parentAvailableWidth = getAvailableWidthForParent(componentStageData, contextHelper)
            const availableWidth = getAvailableWidth(contextHelper, componentPointer)
            const {componentLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {}
            updateTargetLayout(componentStageData, {
                componentLayout: {
                    ...componentLayout,
                    maxWidth: {
                        value: (availableWidth / parentAvailableWidth) * 100,
                        type: 'percentage'
                    },
                    width: {type: 'auto'},
                    type: 'ComponentLayout'
                }
            })
        },
        heuristicCode: 'BUTTON MAX WIDTH'
    },
    {
        predicate: () => true,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateComponentWidth(
                componentStageData,
                contextHelper,
                getAvailableWidth(contextHelper, componentStageData.componentPointer)
            ),
        heuristicCode: 'FALLBACK WIDTH'
    }
]
